import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CRM_API_URL, CRM_DEMO_MODE } from '../config';
import './CrmPage.css';

const PLANS = [
  ['freemium', 'Freemium'],
  ['standard', 'Expert'],
  ['finance', 'Finance'],
  ['premium', 'Vision / Premium'],
  ['vision', 'Vision (ancien plan)'],
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || (CRM_DEMO_MODE ? 'crm-local-demo' : '')}` });

async function readJson(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || 'Le CRM est indisponible.');
    error.status = response.status;
    throw error;
  }
  return data;
}

export default function CrmPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ q: search, page: String(page) });
      const response = await fetch(`${CRM_API_URL}/api/crm/users?${params}`, {
        headers: authHeaders(), signal,
      });
      const result = await readJson(response);
      if (signal.aborted) return;
      setData(result);
      setDrafts(Object.fromEntries(result.users.map((user) => [user.id, user.plan])));
    } catch (failure) {
      if (!signal.aborted) { setData(null); setError(failure.message); }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refresh]);

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const savePlan = async (user) => {
    const plan = drafts[user.id];
    if (!plan || savingId) return;
    setSavingId(user.id);
    setFeedback((previous) => ({ ...previous, [user.id]: '' }));
    try {
      const response = await fetch(`${CRM_API_URL}/api/crm/users/${encodeURIComponent(user.id)}/plan`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, previousPlan: user.plan }),
      });
      const result = await readJson(response);
      setData((previous) => ({
        ...previous,
        users: previous.users.map((item) => item.id === user.id
          ? { ...item, plan: result.plan, subscriptionSource: result.subscriptionSource }
          : item),
      }));
      setFeedback((previous) => ({ ...previous, [user.id]: 'Offre enregistrée.' }));
    } catch (failure) {
      setFeedback((previous) => ({ ...previous, [user.id]: failure.message }));
      if (failure.status === 409) setRefresh((value) => value + 1);
    } finally {
      setSavingId(null);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.matchedUsers / data.pageSize)) : 1;
  return <div className="crm-page">
    <div className="crm-heading">
      <div><Link to="/account" className="crm-back">← Compte</Link><p className="ws-eyebrow">Administration UpyaChat</p><h1>CRM</h1><p>Utilisateurs, conversations et offres attribuées.</p></div>
      <button type="button" className="ws-secondary" onClick={() => setRefresh((value) => value + 1)} disabled={loading}>Actualiser</button>
    </div>
    {error && <p className="crm-error" role="alert">{error}</p>}
    {data && <>
      {data.demo && <p className="crm-demo-note" role="status">Mode démonstration : comptes fictifs. Les modifications restent dans cette session locale et n’affectent pas Scaleway.</p>}
      <section className="crm-metrics" aria-label="Statistiques globales">
        <div><span>Utilisateurs au total</span><strong>{data.totals.users.toLocaleString('fr-FR')}</strong></div>
        <div><span>Conversations au total</span><strong>{data.totals.conversations.toLocaleString('fr-FR')}</strong></div>
      </section>
      <section className="crm-users" aria-label="Liste des utilisateurs">
        <div className="crm-list-heading"><div><h2>Utilisateurs</h2><p>{data.matchedUsers.toLocaleString('fr-FR')} résultat{data.matchedUsers > 1 ? 's' : ''}</p></div>
          <form onSubmit={submitSearch} className="crm-search"><label htmlFor="crm-search">Rechercher un compte</label><div><input id="crm-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Nom, e-mail ou ID" /><button className="ws-primary" type="submit">Rechercher</button></div></form>
        </div>
        {loading ? <p role="status" className="crm-empty">Chargement…</p> : data.users.length === 0 ? <p className="crm-empty">Aucun utilisateur trouvé.</p> :
          <div className="crm-list">
            {data.users.map((user) => <div className="crm-row" key={user.id}>
              <div className="crm-user"><strong>{[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}</strong><span>{user.email}</span><small>ID : {user.id}</small></div>
              <div className="crm-count"><small>Conversations</small><strong>{user.conversationCount}</strong></div>
              <div className="crm-plan"><label htmlFor={`crm-plan-${user.id}`}>Offre</label><select id={`crm-plan-${user.id}`} value={drafts[user.id] || user.plan} onChange={(event) => { setDrafts((previous) => ({ ...previous, [user.id]: event.target.value })); setFeedback((previous) => ({ ...previous, [user.id]: '' })); }} disabled={savingId === user.id}>{PLANS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>{user.subscriptionSource === 'manual' && <small>Attribuée manuellement</small>}</div>
              <div className="crm-action"><button type="button" className="ws-primary" onClick={() => savePlan(user)} disabled={Boolean(savingId) || (drafts[user.id] === user.plan && user.subscriptionSource === 'manual')}>{savingId === user.id ? 'Enregistrement…' : 'Enregistrer'}</button>{feedback[user.id] && <small role="status">{feedback[user.id]}</small>}</div>
            </div>)}
          </div>}
        <div className="crm-pagination"><button type="button" className="ws-secondary" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Précédent</button><span>Page {page} / {totalPages}</span><button type="button" className="ws-secondary" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)}>Suivant</button></div>
      </section>
    </>}
  </div>;
}
